import { useState } from "react";
import type { ReactNode } from "react";
import { Bell, ChevronDown, CircleUserRound, LogOut, Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@erp/ui";

import { SUPPORTED_LOCALES, setLocale } from "@/lib/i18n";
import type { Locale } from "@/lib/i18n";
import { getStoredTheme, setTheme } from "@/lib/theme";
import type { Theme } from "@/lib/theme";
import { useSession } from "@/lib/session/use-session";
import { TopbarScanBox } from "./scan-slot";
import { BranchSwitcher } from "./branch-switcher";

export interface TopbarProps {
  /** Breadcrumb slot — screens render their trail here. */
  children?: ReactNode;
}

const THEME_ORDER: Theme[] = ["light", "dark", "system"];

export function Topbar({ children }: TopbarProps) {
  const { t, i18n } = useTranslation();
  const { user, signOut } = useSession();
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);

  const language = i18n.resolvedLanguage ?? i18n.language;
  const currentLocale: Locale = SUPPORTED_LOCALES.find((l) => l === language) ?? "en";

  const onLocaleChange = (value: string): void => {
    const next = SUPPORTED_LOCALES.find((l) => l === value);
    if (next) void setLocale(next);
  };

  const onThemeChange = (value: string): void => {
    const next = THEME_ORDER.find((candidate) => candidate === value);
    if (!next) return;
    setTheme(next);
    setThemeState(next);
  };

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b bg-background px-4">
      <div className="min-w-0 flex-1">{children}</div>

      <TopbarScanBox />

      <BranchSwitcher />

      {/* Notification center — placeholder until Stage 2. */}
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Disabled buttons swallow pointer events; the span keeps the tooltip alive. */}
          <span className="inline-flex">
            <Button
              variant="ghost"
              size="icon"
              disabled
              aria-label={t("topbar.notifications", "Notifications")}
            >
              <Bell className="size-4" aria-hidden="true" />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {t("topbar.notificationsComingSoon", "Notifications are coming at Stage 2")}
        </TooltipContent>
      </Tooltip>

      <Select value={currentLocale} onValueChange={onLocaleChange}>
        <SelectTrigger className="w-32" aria-label={t("locale.switchLabel")}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_LOCALES.map((locale) => (
            <SelectItem key={locale} value={locale}>
              {t(`locale.${locale}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={t("topbar.theme", "Theme")}>
            <Sun className="size-4 dark:hidden" aria-hidden="true" />
            <Moon className="hidden size-4 dark:block" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuRadioGroup value={theme} onValueChange={onThemeChange}>
            {THEME_ORDER.map((value) => (
              <DropdownMenuRadioItem key={value} value={value}>
                {t(`theme.${value}`)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="gap-2">
            <CircleUserRound className="size-4" aria-hidden="true" />
            <span className="max-w-40 truncate">{user?.display_name}</span>
            <ChevronDown className="size-3 opacity-60" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-56">
          <DropdownMenuLabel className="font-normal text-muted-foreground">
            {t("session.signedInAs", { name: user?.display_name ?? "" })}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => {
              void signOut();
            }}
          >
            <LogOut className="me-2 size-4" aria-hidden="true" />
            {t("actions.signOut")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
